import { splitStepText, formatDureeSecondes, formatTemperatureDEtape } from '@mlc/shared';

/**
 * Texte d'une étape, avec ses ingrédients, ses durées et ses températures
 * surlignés (US-39).
 *
 * Le cuisinier lit l'étape, pas la fiche. Mettre l'information structurée là où
 * le regard est déjà supprime l'aller-retour entre le texte et la liste —
 * exactement le geste qu'on ne peut pas faire avec les mains grasses.
 *
 * Le découpage vit dans `shared/src/highlightStep.ts`, pur et testé. Ce
 * composant n'habille que le résultat : aucune règle de reconnaissance ici,
 * sans quoi elle divergerait de celle qui est vérifiée.
 */
export interface StepTextProps {
  instruction: string;
  /** `name_normalized` des seuls ingrédients associés à l'étape (`step_ingredient`). */
  ingredients?: readonly string[];
  /**
   * Appelé quand une durée est touchée. Absent sur la fiche en lecture, où
   * surligner informe sans rien armer ; fourni en mode cuisson, où le geste
   * lance le minuteur de l'étape.
   */
  onDuree?: (secondes: number) => void;
  className?: string;
}

export function StepText({ instruction, ingredients = [], onDuree, className }: StepTextProps): React.JSX.Element {
  const fragments = splitStepText(instruction, ingredients);

  return (
    <p className={className}>
      {fragments.map((fragment, index) => {
        const cle = `${String(index)}-${fragment.kind}`;

        if (fragment.kind === 'ingredient') {
          return (
            <span key={cle} data-fragment="ingredient" className="font-semibold text-primary">
              {fragment.text}
            </span>
          );
        }

        if (fragment.kind === 'temperature') {
          // L'infobulle ne propose un thermostat que si l'étape parle d'un four
          // (US-100) : sur « 90 °C » d'un bol chauffant, « th. 3 » est un repère
          // pour un appareil qu'on n'utilise pas.
          const complet = typeof fragment.value === 'number'
            ? formatTemperatureDEtape(fragment.value, instruction)
            : null;
          return (
            <span
              key={cle}
              data-fragment="temperature"
              // La position de thermostat est donnée en infobulle plutôt qu'en
              // toutes lettres : elle renseigne sans allonger la phrase.
              title={complet ?? undefined}
              className="font-semibold text-primary"
            >
              {fragment.text}
            </span>
          );
        }

        if (fragment.kind === 'duree') {
          // **En secondes depuis US-98.** Le nom local disait « minutes » et il
          // était passé tel quel à `startTimer` : le renommer était la moitié
          // du travail, l'autre étant que « 20 sec » produise désormais un
          // fragment, ce que le motif de `highlightStep` ne faisait pas.
          const secondes = typeof fragment.value === 'number' ? fragment.value : null;
          if (onDuree === undefined || secondes === null) {
            return (
              <span key={cle} data-fragment="duree" className="font-semibold text-primary">
                {fragment.text}
              </span>
            );
          }
          return (
            <button
              key={cle}
              type="button"
              data-fragment="duree"
              onClick={() => { onDuree(secondes); }}
              aria-label={`Lancer un minuteur de ${formatDureeSecondes(secondes) ?? ''}`}
              // Souligné et non encadré : un bouton dans une phrase ne doit pas
              // casser la ligne de lecture.
              //
              // **La zone de frappe, elle, ne peut pas rester à hauteur de
              // texte.** Le raisonnement d'origine — « le plancher du §4 vise
              // les commandes de l'écran, pas les mots d'un paragraphe » —
              // tient sur la fiche en lecture. Il ne tient pas en mode
              // cuisine, où le même composant est rendu : mesurée sur un
              // téléphone de 390 px, cette cible fait **33 px de haut** sur
              // l'écran dont le §4 dit qu'il s'utilise « avec les mains
              // grasses » et exige 48 px. Et la rater n'est pas neutre : on
              // arme un minuteur de la mauvaise durée, ce qu'on ne remarque
              // qu'à la sonnerie.
              //
              // Un pseudo-élément absolu étend la zone de frappe de 8 px de
              // part et d'autre sans rien changer à la mise en page — c'est
              // exactement la séparation entre marque visuelle et zone de
              // frappe que `ui/Checkbox.tsx` applique depuis US-25, ici
              // transposée à un mot au fil du texte. Du padding vertical, lui,
              // aurait écarté les lignes du paragraphe.
              className="relative font-semibold text-primary underline decoration-dotted underline-offset-4 after:absolute after:inset-x-0 after:-inset-y-2 after:content-['']"
            >
              {fragment.text}
            </button>
          );
        }

        return <span key={cle}>{fragment.text}</span>;
      })}
    </p>
  );
}
